import { useState } from 'react';
import { X, User, Bell, Info, HelpCircle, Palette } from 'lucide-react';
import { HELP_CONTENT, HELP_CATEGORIES } from '../data/helpContent';
import HelpTooltip from './HelpTooltip';
import ThemeSelector from './ThemeSelector';
import changelog from '../data/changelog.json';

const SettingsModal = ({ user, onClose }) => {
  const [activeTab, setActiveTab] = useState('account');

  const tabs = [
    { id: 'account', label: 'Mon compte', icon: User },
    { id: 'appearance', label: 'Apparence', icon: Palette },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'help', label: 'Aide', icon: HelpCircle },
    { id: 'about', label: 'À propos', icon: Info }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b bg-gradient-to-r from-primary to-primary-dark text-white">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">Paramètres</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-primary-darker rounded-full transition"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b bg-gray-50 px-3 sm:px-6 overflow-x-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 sm:px-4 py-3 border-b-2 transition whitespace-nowrap text-sm sm:text-base flex-shrink-0 ${
                  activeTab === tab.id
                    ? 'border-primary text-primary font-semibold'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                }`}
              >
                <Icon size={18} className="flex-shrink-0" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Apparence */}
          {activeTab === 'appearance' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">Thème de couleurs</h3>
                <ThemeSelector />
              </div>
            </div>
          )}

          {/* Mon compte */}
          {activeTab === 'account' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">Informations personnelles</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                    <input
                      type="text"
                      value={user?.name || ''}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={user?.email || ''}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rôle</label>
                    <input
                      type="text"
                      value={user?.role || 'Utilisateur'}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                    />
                  </div>
                  <p className="text-sm text-gray-500">
                    Pour modifier ces informations, contactez votre administrateur ou utilisez votre profil Authentik.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Notifications */}
          {activeTab === 'notifications' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">Notifications push</h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <label className="font-medium">Activer les notifications push</label>
                        <HelpTooltip {...HELP_CONTENT.notificationsPush} />
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        Recevez des alertes en temps réel sur vos appareils pour les ruptures de stock et stocks bas.
                      </p>
                    </div>
                  </div>
                  <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                    <p className="text-sm text-primary-darker">
                      💡 <strong>Astuce</strong> : Vous pouvez activer les notifications directement depuis le bandeau qui apparaît lors de votre première visite. Les notifications sont persistantes et fonctionnent sur plusieurs appareils.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-4">Notifications par email (à venir)</h3>
                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <p className="text-sm text-gray-600">
                      📧 Les notifications par email seront disponibles dans la version 2.2 (Q1 2026). Vous pourrez recevoir :
                    </p>
                    <ul className="list-disc list-inside text-sm text-gray-600 mt-2 space-y-1">
                      <li>Alertes de rupture de stock critiques</li>
                      <li>Résumé quotidien ou hebdomadaire</li>
                      <li>Notifications de changement de prix</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Aide */}
          {activeTab === 'help' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">📚 Documentation complète</h3>
                <p className="text-gray-600 mb-4">
                  Retrouvez des explications détaillées sur tous les concepts de SuiviPlus en cliquant sur les icônes ℹ️ à côté des termes dans l'application.
                </p>
              </div>

              {Object.entries(HELP_CATEGORIES).map(([categoryKey, category]) => (
                <div key={categoryKey} className="border rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">{category.title}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {category.keys.map(key => {
                      const helpItem = HELP_CONTENT[key];
                      if (!helpItem) return null;
                      return (
                        <div key={key} className="flex items-center text-sm text-gray-700">
                          <span className="mr-2">•</span>
                          <span>{helpItem.title}</span>
                          <HelpTooltip {...helpItem} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-900">
                  💚 <strong>Besoin d'aide ?</strong> N'hésitez pas à cliquer sur les icônes ℹ️ bleues présentes partout dans l'application pour obtenir des explications contextuelles.
                </p>
              </div>
            </div>
          )}

          {/* À propos */}
          {activeTab === 'about' && (
            <div className="space-y-6">
              <div className="text-center py-8">
                <div className="text-6xl mb-4">📦</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">SuiviPlus</h3>
                <p className="text-gray-600 mb-4">Gestion complète pour micro-crèches</p>
                <div className="inline-block bg-primary/10 text-primary-dark px-4 py-2 rounded-full font-semibold">
                  Version {changelog.currentVersion}
                </div>
                {changelog.currentDate && (
                  <p className="text-xs text-gray-500 mt-2">
                    Mise en production : {new Date(changelog.currentDate).toLocaleDateString('fr-FR')}
                  </p>
                )}
              </div>

              {(() => {
                const current = (changelog.versions || []).find(
                  (v) => v.version === changelog.currentVersion
                );
                const next = changelog.unreleased;
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-semibold mb-2">🚀 Dernières nouveautés</h4>
                      {current?.highlights?.length ? (
                        <ul className="text-sm text-gray-600 space-y-1">
                          {current.highlights.slice(0, 5).map((h, i) => (
                            <li key={i}>{h}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-500 italic">
                          Voir l'historique complet des versions.
                        </p>
                      )}
                    </div>

                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-semibold mb-2">📅 À venir</h4>
                      {next?.highlights?.length ? (
                        <ul className="text-sm text-gray-600 space-y-1">
                          {next.highlights.slice(0, 5).map((h, i) => (
                            <li key={i}>{h}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-500 italic">
                          Pas de chantier annoncé pour le moment.
                        </p>
                      )}
                    </div>
                  </div>
                );
              })()}

              <div className="border-t pt-6">
                <h4 className="font-semibold mb-3">📞 Support</h4>
                <div className="space-y-2 text-sm text-gray-600">
                  <p>
                    <strong>GitHub :</strong>{' '}
                    <a
                      href="https://github.com/neotima/delogic_platform01"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      neotima/delogic_platform01
                    </a>
                  </p>
                  <p><strong>Made with ❤️ in 🇫🇷</strong></p>
                  <p className="text-xs text-gray-500">© 2026 SuiviPlus - Tous droits réservés</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
